import { AgentGatewayState } from "@/components/agents/AgentGatewayState";
import {
  type AgentDashboardView,
  AgentSessionsDashboard,
  AgentSessionsDashboardSkeleton,
} from "@/components/agents/AgentSessionsDashboard";
import type { NewAgentSessionInput } from "@/components/agents/NewAgentSessionDialog";
import {
  agentGatewayQueryKeys,
  agentSessionsQueryOptions,
  availableAgentsQueryOptions,
} from "@/components/agents/query-options";
import { useRouteHeadingFocus } from "@/components/agents/use-route-heading-focus";
import LoadingSpinner from "@/components/common/pages/loading-spinner";
import { ApiError } from "@/lib/api/api-error";
import { buildMetadata } from "@/lib/metadata";
import { AgentGatewayService } from "@/services/agent-gateway-service";
import {
  type AvailableAgent,
  isAvailableAgent,
} from "@/services/types/agent-gateway";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { Ref } from "react";
import { z } from "zod";
import { requireAgentGatewayAccess, selectAvailableAgent } from "./-access";

export const Route = createFileRoute("/_main/sales-agent/")({
  beforeLoad: ({ context }) => requireAgentGatewayAccess(context.auth),
  component: SalesAgentPage,
  validateSearch: z.object({
    agent: z.string().min(1).optional(),
    view: z.enum(["sessions", "research-targets"]).optional(),
  }),
  head: () => ({
    meta: buildMetadata({
      title: "AI Agents",
      description: "Run and resume supplier-owned AI agent sessions.",
      keywords: ["AI agents", "agent sessions", "sales agent"],
    }),
  }),
});

function getGatewayErrorMessage(error: Error, fallback: string) {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "Your account is not permitted to access this agent.";
    }

    if (error.status === 404) {
      return "This agent is no longer available to your supplier.";
    }
  }

  return fallback;
}

export function SalesAgentPage() {
  const headingRef = useRouteHeadingFocus<HTMLHeadingElement>("sales-agent");
  const agentsQuery = useQuery(availableAgentsQueryOptions());

  if (agentsQuery.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center" aria-busy="true">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  if (agentsQuery.isError) {
    const forbidden =
      agentsQuery.error instanceof ApiError && agentsQuery.error.status === 403;

    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description={
            forbidden
              ? "Your account is not permitted to use supplier AI agents."
              : "AI agents could not be loaded. Please try again shortly."
          }
          kind={forbidden ? "forbidden" : "unavailable"}
          onAction={forbidden ? undefined : () => agentsQuery.refetch()}
          title={forbidden ? "Access denied" : "AI agents are unavailable"}
        />
      </div>
    );
  }

  const availableAgents = agentsQuery.data.filter(isAvailableAgent);

  if (availableAgents.length === 0) {
    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description="No AI agents are currently assigned to your supplier. Contact an OpenFarming administrator if you need access."
          kind="empty"
          title="No AI agents available"
        />
      </div>
    );
  }

  return (
    <AvailableAgentSessionsPage
      agents={availableAgents}
      headingRef={headingRef}
    />
  );
}
export function AvailableAgentSessionsPage({
  agents,
  headingRef,
}: {
  agents: AvailableAgent[];
  headingRef: Ref<HTMLHeadingElement>;
}) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const selectedAgent = selectAvailableAgent(agents, search.agent);
  const activeView: AgentDashboardView = search.view ?? "sessions";
  const sessionsQuery = useQuery({
    ...agentSessionsQueryOptions(selectedAgent.key),
    enabled: activeView === "sessions",
  });
  const createSessionMutation = useMutation({
    mutationFn: ({ agentKey, sessionName, campaign }: NewAgentSessionInput) =>
      AgentGatewayService.createSession(agentKey, {
        session_name: sessionName,
        ...(campaign
          ? {
              initial_context: {
                campaign: {
                  target_segments: campaign.targetSegments,
                  requested_company_count: campaign.requestedCompanyCount,
                  ...(campaign.discoveryMode
                    ? { discovery_mode: campaign.discoveryMode }
                    : {}),
                },
              },
            }
          : {}),
      }),
    onSuccess: (session, { agentKey }) => {
      const sessionsQueryKey = agentGatewayQueryKeys.sessions(agentKey);
      queryClient.setQueryData(
        sessionsQueryKey,
        (current: typeof sessionsQuery.data) =>
          current?.some((item) => item.id === session.id)
            ? current
            : [session, ...(current ?? [])],
      );
      queryClient.setQueryData(
        agentGatewayQueryKeys.session(agentKey, session.id),
        session,
      );
      void queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      navigate({
        to: "/sales-agent/$agentKey/$sessionId",
        params: {
          agentKey,
          sessionId: session.id,
        },
        search: {},
      });
    },
  });

  const handleAgentChange = (agentKey: string) => {
    createSessionMutation.reset();
    navigate({
      search: { agent: agentKey, view: search.view },
      replace: true,
    });
  };

  const handleViewChange = (view: AgentDashboardView) => {
    navigate({
      search: {
        agent: selectedAgent.key,
        view: view === "sessions" ? undefined : view,
      },
      replace: true,
    });
  };

  if (activeView === "sessions" && sessionsQuery.isPending) {
    return <AgentSessionsDashboardSkeleton />;
  }

  if (activeView === "sessions" && sessionsQuery.isError) {
    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description={getGatewayErrorMessage(
            sessionsQuery.error,
            `${selectedAgent.display_name} sessions could not be loaded. Please try again shortly.`,
          )}
          kind="unavailable"
          onAction={() => sessionsQuery.refetch()}
          title="Sessions are unavailable"
        />
      </div>
    );
  }

  return (
    <AgentSessionsDashboard
      agents={agents}
      activeView={activeView}
      createError={
        createSessionMutation.isError
          ? getGatewayErrorMessage(
              createSessionMutation.error,
              "The new session could not be started. Please try again.",
            )
          : undefined
      }
      isCreating={createSessionMutation.isPending}
      headingRef={headingRef}
      key={selectedAgent.key}
      onAgentChange={handleAgentChange}
      onCreateSession={(input) => createSessionMutation.mutate(input)}
      onResetCreateError={() => createSessionMutation.reset()}
      onViewChange={handleViewChange}
      selectedAgent={selectedAgent}
      sessions={sessionsQuery.data ?? []}
    />
  );
}
